import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { FormatPrefs } from "../lib/i18n.ts";
import { formatTime as fmtTimeI18n } from "../lib/i18n.ts";
import type { CalendarEventAppt } from "./CalendarEvent.tsx";
import { decodeEntities } from "../lib/decode-entities.ts";

interface Props {
  appt: CalendarEventAppt;
  /** Bounding rect of the source event element. */
  anchor: DOMRect;
  formatPrefs?: FormatPrefs;
}

function fmtTime(iso: string, prefs?: FormatPrefs): string {
  if (prefs) return fmtTimeI18n(iso, prefs);
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/**
 * Floating tooltip rendered as a portal-like fixed div positioned near
 * the source event. Defaults to ABOVE the card horizontally aligned to
 * its left edge — this avoids covering neighbouring staff columns.
 * Falls back to below when there's no room above; clamps to viewport
 * on both axes.
 */
export function EventTooltip({ appt, anchor, formatPrefs }: Props) {
  const [pos, setPos] = useState<{ top: number; left: number }>(() => ({
    top: Math.max(8, anchor.top - 188),
    left: anchor.left,
  }));

  useEffect(() => {
    const tooltipWidth = 320;
    const tooltipHeight = 200;
    const margin = 8;

    // Default: above the card, anchored to its left edge.
    let left = anchor.left;
    let top = anchor.top - tooltipHeight - margin;

    // If not enough room above, flip below.
    if (top < margin) {
      top = anchor.bottom + margin;
    }
    // If neither above nor below fits, clamp to viewport top.
    if (top + tooltipHeight > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - tooltipHeight - margin);
    }

    // Clamp horizontal: if right edge would overflow, shift left.
    if (left + tooltipWidth > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - tooltipWidth - margin);
    }
    if (left < margin) left = margin;

    setPos({ top, left });
  }, [anchor.left, anchor.right, anchor.top, anchor.bottom]);

  const notesPreview = appt.notes ? appt.notes.slice(0, 120) + (appt.notes.length > 120 ? "…" : "") : null;

  // Render via portal to document.body so the tooltip escapes the
  // calendar grid's stacking context — otherwise lane-packed event
  // cards (which create their own absolutely-positioned stacking
  // contexts via z-index) can render on top of the tooltip.
  return createPortal(
    <div
      role="tooltip"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        zIndex: 9999,
        background: "var(--paper-raised)",
        border: "1px solid var(--rule)",
        boxShadow: "var(--shadow-md)",
        borderRadius: 10,
        padding: "12px 14px",
        width: 320,
        maxWidth: "calc(100vw - 16px)",
        pointerEvents: "none",
        fontSize: 14,
        lineHeight: 1.45,
        color: "var(--ink)",
        wordBreak: "break-word",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 15 }}>{decodeEntities(appt.customer_name)}</div>
      <div style={{ color: "var(--ink-soft)", fontSize: 13, marginBottom: 6, fontVariantNumeric: "tabular-nums" }}>
        {fmtTime(appt.start_at, formatPrefs)}–{fmtTime(appt.end_at, formatPrefs)} · {decodeEntities(appt.service_name)}
      </div>
      <div style={{ color: "var(--ink-soft)", fontSize: 13, marginBottom: 6 }}>
        with {appt.staff_name}
      </div>
      {(appt.customer_phone || appt.customer_email) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: notesPreview ? 6 : 0, fontSize: 13, color: "var(--ink-soft)" }}>
          {appt.customer_phone && <span>📞 {appt.customer_phone}</span>}
          {appt.customer_email && <span>✉ {appt.customer_email}</span>}
        </div>
      )}
      {notesPreview && (
        <div
          style={{
            marginTop: 6,
            paddingTop: 6,
            borderTop: "1px solid var(--rule)",
            fontSize: 13,
            color: "var(--ink-soft)",
            fontStyle: "italic",
          }}
        >
          {notesPreview}
        </div>
      )}
    </div>,
    document.body,
  );
}
